import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import {
  COMMISSION_STATUS_LABELS,
  COMMISSION_STATUS_TONES,
  REFERRAL_PIPELINE,
  REFERRAL_STATUS_LABELS,
  REFERRAL_STATUS_TONES,
} from '../../constants/partnerNetwork';
import { PartnerReferral, getMyReferral } from '../../services/partnerPortal';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatRate,
} from '../../utils/partnerFormat';
import { Alert, Pipeline, Spinner, StatusBadge } from '../partner-network/ui';

const PartnerReferralDetail: React.FC = () => {
  const { id = '' } = useParams();

  const [referral, setReferral] = useState<PartnerReferral | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    getMyReferral(id)
      .then((response) => {
        if (!cancelled) setReferral(response);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load the referral');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    );
  }

  if (!referral) {
    return (
      <section className="py-12">
        <div className="mx-auto max-w-2xl px-4">
          <Alert tone="error">{error || 'Referral not found.'}</Alert>
          <Link to="/partner/referrals" className="btn-secondary mt-6 py-2.5 px-5">
            Back to my referrals
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Link
          to="/partner/referrals"
          className="mb-6 inline-flex items-center gap-2 text-sm text-muted transition-colors hover:text-white"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M15 19l-7-7 7-7" />
          </svg>
          My referrals
        </Link>

        <AnimatedSection animation="slide-up">
          <div className="mb-6 rounded-2xl border border-surface-border bg-surface-card p-6">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <h1 className="font-display text-2xl font-semibold text-white">
                {referral.businessName}
              </h1>
              <StatusBadge
                label={REFERRAL_STATUS_LABELS[referral.status]}
                tone={REFERRAL_STATUS_TONES[referral.status]}
              />
            </div>
            <p className="text-sm text-muted">
              {[referral.businessType, referral.city, referral.country]
                .filter(Boolean)
                .join(' · ') || 'No business details given'}
            </p>
            <p className="mt-1 text-xs text-muted/70">
              <span className="font-mono">{referral.referralId}</span> · submitted{' '}
              {formatDate(referral.submittedAt)}
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-5 text-sm font-semibold uppercase tracking-wider text-muted">
                Where it stands
              </h2>
              <Pipeline
                stages={REFERRAL_PIPELINE}
                labels={REFERRAL_STATUS_LABELS}
                current={referral.status}
                offTrackNote={
                  referral.status === 'lost'
                    ? 'This one did not go ahead. Thank you for the introduction all the same.'
                    : undefined
                }
              />
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                What you told us
              </h2>
              <dl className="space-y-4 text-sm">
                <div>
                  <dt className="mb-1 text-muted">The problem</dt>
                  <dd className="whitespace-pre-wrap text-white">{referral.problem}</dd>
                </div>
                {referral.additionalInformation && (
                  <div>
                    <dt className="mb-1 text-muted">Additional information</dt>
                    <dd className="whitespace-pre-wrap text-white">
                      {referral.additionalInformation}
                    </dd>
                  </div>
                )}
                {referral.identifiedHow && (
                  <div>
                    <dt className="mb-1 text-muted">How you found it</dt>
                    <dd className="whitespace-pre-wrap text-white">
                      {referral.identifiedHow}
                    </dd>
                  </div>
                )}
                <div className="border-t border-surface-border pt-4">
                  <dt className="mb-1 text-muted">Contact you gave us</dt>
                  <dd className="text-white">
                    {referral.contactName || '—'}
                    {referral.contactEmail && (
                      <span className="block text-muted">{referral.contactEmail}</span>
                    )}
                    {referral.contactPhone && (
                      <span className="block text-muted">{referral.contactPhone}</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-2xl border border-surface-border bg-surface-card p-6">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
                Timeline
              </h2>
              {referral.timeline.length === 0 ? (
                <p className="text-sm text-muted">Nothing recorded yet.</p>
              ) : (
                <ol className="space-y-0">
                  {[...referral.timeline].reverse().map((entry, index, all) => (
                    <li key={`${entry.status}-${index}`} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <span
                          className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                            index === 0 ? 'bg-primary-400' : 'bg-surface-border'
                          }`}
                        />
                        {index < all.length - 1 && (
                          <span aria-hidden className="w-px flex-1 bg-surface-border" />
                        )}
                      </div>
                      <div className="pb-5">
                        <p className="text-sm text-white">{entry.label}</p>
                        <p className="mt-0.5 text-xs text-muted">
                          {formatDateTime(entry.createdAt)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-surface-border bg-surface-card p-6 lg:h-fit">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted">
              Your commission
            </h2>

            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Project value</dt>
                <dd className="text-white">
                  {referral.projectValue !== null
                    ? formatMoney(referral.projectValue)
                    : 'Not agreed yet'}
                </dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Your rate</dt>
                <dd className="text-white">{formatRate(referral.commissionRate)}</dd>
              </div>
              <div className="flex justify-between gap-3 border-t border-surface-border pt-3">
                <dt className="text-muted">
                  {referral.projectValue !== null ? 'Commission' : 'Potential commission'}
                </dt>
                <dd className="font-medium text-primary-400">
                  {formatMoney(referral.potentialCommission)}
                </dd>
              </div>
            </dl>

            {referral.commissionStatus ? (
              <div className="mt-5 space-y-3 border-t border-surface-border pt-5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted">Status</span>
                  <StatusBadge
                    label={COMMISSION_STATUS_LABELS[referral.commissionStatus]}
                    tone={COMMISSION_STATUS_TONES[referral.commissionStatus]}
                  />
                </div>
                {referral.commissionDueDate && (
                  <div className="flex justify-between gap-3">
                    <span className="text-muted">Due</span>
                    <span className="text-white">
                      {formatDate(referral.commissionDueDate)}
                    </span>
                  </div>
                )}
                {referral.commissionPaidDate && (
                  <div className="flex justify-between gap-3">
                    <span className="text-muted">Paid</span>
                    <span className="text-white">
                      {formatDate(referral.commissionPaidDate)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-5 border-t border-surface-border pt-5 text-xs text-muted">
                {referral.projectValue !== null
                  ? 'Your commission is being processed.'
                  : 'The figure above is an estimate based on the value you gave us. Your commission is confirmed once the project value is agreed with the business.'}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PartnerReferralDetail;
